import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Button, Result } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { PaymentAPI } from '../backend/api';

const PaymentResultPage = () => {
  const { owner, paymentName } = useParams();
  const [searchParams] = useSearchParams();
  const subscription = searchParams.get('subscription');
  const transactionOwner = searchParams.get('transactionOwner');
  const transactionName = searchParams.get('transactionName');
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending'); // pending, success, error
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyOrder = async () => {
      // If we have success indicators from PayPal redirect, set success immediately
      if (subscription || transactionOwner || transactionName) {
        // Optionally verify with backend
        if (owner && paymentName) {
          try {
            const orderRes = await PaymentAPI.verifyPayment(`${owner}/${paymentName}`);
            const order = orderRes?.data?.data;
            if (order && order.state === 'Paid') {
              setStatus('success');
              setLoading(false);
              return;
            }
          } catch (e) {
            // Backend verification failed, but URL params indicate success — trust the redirect
            console.warn('Order verification failed, falling back to URL params', e);
          }
        }
        setStatus('success');
        setLoading(false);
        return;
      }

      // No success params — try to verify with backend
      if (owner && paymentName) {
        try {
          const orderRes = await PaymentAPI.verifyPayment(`${owner}/${paymentName}`);
          const order = orderRes?.data?.data;
          if (order) {
            if (order.state === 'Paid' || order.state === 'Completed') {
              setStatus('success');
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          console.warn('Order verification failed, showing pending state', e);
        }
      }

      // Default to pending — user can check status manually
      setStatus('pending');
      setLoading(false);
    };

    verifyOrder();
  }, [subscription, transactionOwner, transactionName, owner, paymentName]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f0f23', padding: '100px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin tip="Processing payment..." />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f23', padding: '100px 20px' }}>
      {status === 'success' && (
        <Result
          status="success"
          title="Payment successful!"
          subTitle="You now have access to all content."
          extra={[
            <Button key="home" onClick={() => navigate('/')}>
              Go to Home
            </Button>,
          ]}
          icon={<CheckCircleOutlined style={{ fontSize: 48 }} />}
        />
      )}
      {status === 'error' && (
        <Result
          status="error"
          title="Something went wrong"
          subTitle="Your payment could not be processed."
          extra={[
            <Button key="tryAgain" onClick={() => navigate('/payment')}>
              Try Again
            </Button>,
            <Button key="support" style={{ marginLeft: 8 }}>
              Contact Support
            </Button>,
          ]}
          icon={<CloseCircleOutlined style={{ fontSize: 48 }} />}
        />
      )}
      {status === 'pending' && (
        <Result
          status="info"
          title="Processing payment..."
          subTitle="This may take a moment."
          extra={[
            <Button key="checkStatus" onClick={() => window.location.reload()}>
              Check Status
            </Button>,
          ]}
          icon={<ClockCircleOutlined style={{ fontSize: 48 }} />}
        />
      )}
    </div>
  );
};

export default PaymentResultPage;